// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Form-based field editor: fields, groups, and tables with keyboard-accessible reordering (requirements 3.2, 3.3, 3.6).
import { createContext, useContext, useLayoutEffect, useRef, type Dispatch } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import {
  MAX_OPTION_LENGTH,
  MAX_OPTIONS,
  MIN_OPTIONS,
  type BlueprintFieldNode,
  type FieldType,
  type InferenceType,
  type ScalarField,
  type ValidationErrors,
} from '@bda/shared';
import type { Action, NodePath } from './editorState';

/**
 * Editor types (task 13.7). "Choice" and "Choice list" are text fields with allowed values (`options`), which BDA
 * receives as `enum`; the rest map one-to-one to a FieldType.
 */
type TypeKey = FieldType | 'choice' | 'choice[]';
// The original five keep their positions, so arrow-key habits in the list still hold; the task 13.7 types follow.
const TYPE_OPTIONS: Array<{ value: TypeKey; label: string; description?: string }> = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'integer', label: 'Whole number' },
  { value: 'boolean', label: 'Yes/No' },
  { value: 'string[]', label: 'List of text' },
  { value: 'choice', label: 'Choice', description: 'One value from a list you set' },
  { value: 'choice[]', label: 'Choice list', description: 'Any number of values from a list you set' },
  { value: 'number[]', label: 'List of numbers' },
  { value: 'integer[]', label: 'List of whole numbers' },
  { value: 'boolean[]', label: 'List of yes/no' },
];

const typeKey = (f: Pick<ScalarField, 'type' | 'options'>): TypeKey =>
  f.options !== undefined && f.type === 'string'
    ? 'choice'
    : f.options !== undefined && f.type === 'string[]'
      ? 'choice[]'
      : f.type;

/** The editor's name for a field's type ("Choice", "List of numbers", ...). */
export const fieldTypeLabel = (f: Pick<ScalarField, 'type' | 'options'>): string =>
  TYPE_OPTIONS.find((o) => o.value === typeKey(f))?.label ?? f.type;

/** The patch for choosing a type: choices keep (or start) their allowed values; other types drop them. */
export function typePatch(field: Pick<ScalarField, 'options'>, key: TypeKey): Pick<ScalarField, 'type' | 'options'> {
  if (key === 'choice') return { type: 'string', options: field.options ?? [] };
  if (key === 'choice[]') return { type: 'string[]', options: field.options ?? [] };
  return { type: key, options: undefined };
}

/** Allowed values are edited one per line; an empty box is no values (not one blank value). */
export const parseOptionLines = (text: string): string[] => (text === '' ? [] : text.split(/\r?\n/));
const INFERENCE_OPTIONS: Array<{ value: InferenceType; label: string; description: string }> = [
  { value: 'explicit', label: 'Explicit', description: 'Extract the value as written' },
  { value: 'inferred', label: 'Inferred', description: 'Derive the value from the document' },
];

/**
 * FormField labels a control with aria-labelledby, which wins over the control's own aria-label in the browser,
 * so every field's controls would be announced alike ("Instruction"). A visually hidden prefix names the field.
 */
const labelFor = (name: string, text: string) => (
  <>
    {name && <span className="sr-only">{name}</span>} {text}
  </>
);

/**
 * Keeps keyboard focus in place after structural changes (task 12.2). Rows are keyed by position, so without
 * this a moved field leaves focus on the button of whatever field now sits in its old slot, and removing a field
 * drops focus to the page body. Targets are `data-focus` keys on the controls; the first one present and enabled
 * after the next render gets focus.
 */
type FocusRequest = (targets: string[]) => void;
const FocusCtx = createContext<FocusRequest>(() => undefined);

const pathKey = (path: readonly number[]): string => path.join('.');
const focusKey = (path: readonly number[] | 'root', action: string): string =>
  `${path === 'root' ? 'root' : pathKey(path)}:${action}`;

/** Where focus goes after moving the node at `path` by `delta`. */
export function focusAfterMove(path: NodePath, delta: -1 | 1, count: number): string[] {
  const to = [...path] as number[];
  to[to.length - 1] = path[path.length - 1]! + delta;
  const idx = to[to.length - 1]!;
  const same = delta < 0 ? 'up' : 'down';
  const other = delta < 0 ? 'down' : 'up';
  // At either end the button just used becomes disabled, so fall back to the other direction.
  return (delta < 0 && idx === 0) || (delta > 0 && idx === count - 1)
    ? [focusKey(to, other), focusKey(to, same)]
    : [focusKey(to, same), focusKey(to, other)];
}

/** Where focus goes after removing the node at `path`: the node that takes its place, the one before, or the add button. */
export function focusAfterRemove(path: NodePath): string[] {
  const i = path[path.length - 1]!;
  const parent = path.length === 2 ? [path[0]] : null;
  const at = (j: number) => (parent ? [parent[0]!, j] : [j]);
  return [
    focusKey(at(i), 'remove'),
    ...(i > 0 ? [focusKey(at(i - 1), 'remove')] : []),
    focusKey(parent ?? 'root', 'add'),
  ];
}

function useFocusRequest(): FocusRequest {
  return useContext(FocusCtx);
}

const errorKey = (path: NodePath, child: 'fields' | 'columns' | null, prop: string): string =>
  path.length === 1 ? `fields.${path[0]}.${prop}` : `fields.${path[0]}.${child}.${path[1]}.${prop}`;

interface ScalarProps {
  field: ScalarField;
  path: NodePath;
  child: 'fields' | 'columns' | null;
  index: number;
  count: number;
  errors: ValidationErrors;
  dispatch: Dispatch<Action>;
  label: string;
  readOnly: boolean;
}

function ScalarEditor({ field, path, child, index, count, errors, dispatch, label, readOnly }: ScalarProps) {
  const focus = useFocusRequest();
  const type = TYPE_OPTIONS.find((o) => o.value === typeKey(field))!;
  const inference = INFERENCE_OPTIONS.find((o) => o.value === field.inferenceType)!;
  return (
    <SpaceBetween size="s">
      <ColumnLayout columns={3}>
        <FormField label={`${label} name`} errorText={errors[errorKey(path, child, 'name')]}>
          <Input
            data-focus={focusKey(path, 'name')}
            value={field.name}
            readOnly={readOnly}
            ariaLabel={`${label} name`}
            onChange={(e) => dispatch({ type: 'update', path, patch: { name: e.detail.value } })}
          />
        </FormField>
        <FormField label={labelFor(field.name, 'Type')}>
          <Select
            selectedOption={type}
            options={TYPE_OPTIONS}
            disabled={readOnly}
            ariaLabel={`${field.name} type`}
            onChange={(e) => {
              const key = e.detail.selectedOption.value as TypeKey;
              const patch = typePatch(field, key);
              // A new choice field: put focus in its allowed values so they can be typed straight away.
              if (patch.options !== undefined && field.options === undefined) focus([focusKey(path, 'options')]);
              dispatch({ type: 'update', path, patch });
            }}
          />
        </FormField>
        <FormField label={labelFor(field.name, 'Inference')}>
          <Select
            selectedOption={inference}
            options={INFERENCE_OPTIONS}
            disabled={readOnly}
            ariaLabel={`${field.name} inference`}
            onChange={(e) =>
              dispatch({
                type: 'update',
                path,
                patch: { inferenceType: e.detail.selectedOption.value as InferenceType },
              })
            }
          />
        </FormField>
      </ColumnLayout>
      <FormField
        label={labelFor(field.name, 'Instruction')}
        description="Tell the model what to extract, in one or two sentences."
        errorText={errors[errorKey(path, child, 'instruction')]}
      >
        <Textarea
          value={field.instruction}
          readOnly={readOnly}
          rows={2}
          ariaLabel={`${field.name} instruction`}
          onChange={(e) => dispatch({ type: 'update', path, patch: { instruction: e.detail.value } })}
        />
      </FormField>
      {field.options !== undefined && (
        <FormField
          label={labelFor(field.name, 'Allowed values')}
          description={`One value per line, as printed on the document. ${MIN_OPTIONS}-${MAX_OPTIONS} values, up to ${MAX_OPTION_LENGTH} characters each.`}
          constraintText={`${field.options.filter((o) => o.trim() !== '').length} of ${MAX_OPTIONS} values`}
          errorText={errors[errorKey(path, child, 'options')]}
        >
          <Textarea
            data-focus={focusKey(path, 'options')}
            value={field.options.join('\n')}
            readOnly={readOnly}
            rows={Math.min(Math.max(field.options.length + 1, 3), 8)}
            ariaLabel={`${field.name} allowed values`}
            placeholder={'Residential\nCommercial\nTemporary construction'}
            onChange={(e) => dispatch({ type: 'update', path, patch: { options: parseOptionLines(e.detail.value) } })}
          />
        </FormField>
      )}
      {!readOnly && (
        <MoveRemove
          label={field.name || `${label.toLowerCase()} ${index + 1}`}
          path={path}
          index={index}
          count={count}
          dispatch={dispatch}
        />
      )}
    </SpaceBetween>
  );
}

function MoveRemove({
  label,
  path,
  index,
  count,
  dispatch,
}: {
  label: string;
  path: NodePath;
  index: number;
  count: number;
  dispatch: Dispatch<Action>;
}) {
  const focus = useFocusRequest();
  const move = (delta: -1 | 1) => {
    focus(focusAfterMove(path, delta, count));
    dispatch({ type: 'move', path, delta });
  };
  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Button
        data-focus={focusKey(path, 'up')}
        iconName="angle-up"
        variant="icon"
        ariaLabel={`Move ${label} up`}
        disabled={index === 0}
        onClick={() => move(-1)}
      />
      <Button
        data-focus={focusKey(path, 'down')}
        iconName="angle-down"
        variant="icon"
        ariaLabel={`Move ${label} down`}
        disabled={index === count - 1}
        onClick={() => move(1)}
      />
      <Button
        data-focus={focusKey(path, 'remove')}
        iconName="remove"
        variant="icon"
        ariaLabel={`Remove ${label}`}
        onClick={() => {
          focus(focusAfterRemove(path));
          dispatch({ type: 'remove', path });
        }}
      />
    </SpaceBetween>
  );
}

function NodeEditor({
  node,
  index,
  count,
  errors,
  dispatch,
  readOnly,
}: {
  node: BlueprintFieldNode;
  index: number;
  count: number;
  errors: ValidationErrors;
  dispatch: Dispatch<Action>;
  readOnly: boolean;
}) {
  const focus = useFocusRequest();
  if (node.kind === 'field') {
    return (
      <Container header={<Header variant="h3">{node.name || 'Unnamed field'}</Header>}>
        <ScalarEditor
          field={node}
          path={[index]}
          child={null}
          index={index}
          count={count}
          errors={errors}
          dispatch={dispatch}
          label="Field"
          readOnly={readOnly}
        />
      </Container>
    );
  }
  const child = node.kind === 'group' ? 'fields' : 'columns';
  const list = node.kind === 'group' ? node.fields : node.columns;
  const childLabel = node.kind === 'group' ? 'Field' : 'Column';
  return (
    <Container
      header={
        <Header
          variant="h3"
          description={node.kind === 'group' ? 'Group of related fields' : 'Table of repeating rows'}
          actions={
            !readOnly && (
              <MoveRemove
                label={node.name || `${node.kind} ${index + 1}`}
                path={[index]}
                index={index}
                count={count}
                dispatch={dispatch}
              />
            )
          }
        >
          {node.name || (node.kind === 'group' ? 'Unnamed group' : 'Unnamed table')}
        </Header>
      }
    >
      <SpaceBetween size="m">
        <ColumnLayout columns={2}>
          <FormField
            label={node.kind === 'group' ? 'Group name' : 'Table name'}
            errorText={errors[`fields.${index}.name`]}
          >
            <Input
              data-focus={focusKey([index], 'name')}
              value={node.name}
              readOnly={readOnly}
              ariaLabel={`${node.kind} name`}
              onChange={(e) => dispatch({ type: 'update', path: [index], patch: { name: e.detail.value } })}
            />
          </FormField>
          {node.kind === 'group' ? (
            <FormField label={labelFor(node.name, 'Description')} errorText={errors[`fields.${index}.description`]}>
              <Input
                value={node.description}
                readOnly={readOnly}
                ariaLabel={`${node.name} description`}
                onChange={(e) => dispatch({ type: 'update', path: [index], patch: { description: e.detail.value } })}
              />
            </FormField>
          ) : (
            <FormField label={labelFor(node.name, 'Instruction')} errorText={errors[`fields.${index}.instruction`]}>
              <Input
                value={node.instruction}
                readOnly={readOnly}
                ariaLabel={`${node.name} instruction`}
                onChange={(e) => dispatch({ type: 'update', path: [index], patch: { instruction: e.detail.value } })}
              />
            </FormField>
          )}
        </ColumnLayout>
        {errors[`fields.${index}.${child}`] && (
          <Box color="text-status-error">{errors[`fields.${index}.${child}`]}</Box>
        )}
        {list.map((f, j) => (
          <Box key={j} padding={{ left: 'l' }}>
            <ScalarEditor
              field={f}
              path={[index, j]}
              child={child}
              index={j}
              count={list.length}
              errors={errors}
              dispatch={dispatch}
              label={childLabel}
              readOnly={readOnly}
            />
          </Box>
        ))}
        {!readOnly && (
          <Button
            data-focus={focusKey([index], 'add')}
            iconName="add-plus"
            onClick={() => {
              focus([focusKey([index, list.length], 'name')]);
              dispatch({ type: 'addChild', parent: index });
            }}
          >
            Add {childLabel.toLowerCase()} to {node.name || node.kind}
          </Button>
        )}
      </SpaceBetween>
    </Container>
  );
}

export function FieldsEditor({
  fields,
  errors,
  dispatch,
  readOnly = false,
}: {
  fields: BlueprintFieldNode[];
  errors: ValidationErrors;
  dispatch: Dispatch<Action>;
  readOnly?: boolean;
}) {
  const root = useRef<HTMLDivElement>(null);
  const pending = useRef<string[] | null>(null);
  const request: FocusRequest = (targets) => {
    pending.current = targets;
  };
  useLayoutEffect(() => {
    const targets = pending.current;
    if (!targets || !root.current) return;
    pending.current = null;
    for (const t of targets) {
      const el = root.current.querySelector<HTMLElement>(`[data-focus="${t}"]`);
      // Cloudscape puts data attributes on the button itself but on the wrapper of an input.
      const target =
        el && (el.matches('button, input, textarea') ? el : el.querySelector<HTMLElement>('input, textarea, button'));
      if (target && !(target as HTMLButtonElement).disabled) {
        target.focus();
        return;
      }
    }
  }, [fields]);
  const add = (kind: 'field' | 'group' | 'table') => {
    request([focusKey([fields.length], 'name')]);
    dispatch({ type: 'add', kind });
  };
  return (
    <FocusCtx.Provider value={request}>
      <div ref={root}>
        <SpaceBetween size="m">
          {errors.fields && <Box color="text-status-error">{errors.fields}</Box>}
          {fields.map((n, i) => (
            <NodeEditor
              key={i}
              node={n}
              index={i}
              count={fields.length}
              errors={errors}
              dispatch={dispatch}
              readOnly={readOnly}
            />
          ))}
          {fields.length === 0 && <Box color="text-body-secondary">No fields yet.</Box>}
          {!readOnly && (
            <SpaceBetween direction="horizontal" size="xs">
              <Button data-focus={focusKey('root', 'add')} iconName="add-plus" onClick={() => add('field')}>
                Add field
              </Button>
              <Button iconName="add-plus" onClick={() => add('group')}>
                Add group
              </Button>
              <Button iconName="add-plus" onClick={() => add('table')}>
                Add table
              </Button>
            </SpaceBetween>
          )}
        </SpaceBetween>
      </div>
    </FocusCtx.Provider>
  );
}
